import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LivechatService } from './livechat.service';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

@Component({
    selector: 'app-livechat',
    templateUrl: './livechat.component.html',
    styleUrls: ['./livechat.component.scss'],
    standalone: false
})
export class LivechatComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('chatBotButton') chatBotButtonRef!: ElementRef<HTMLElement>;

  widgetID = 'widget-my-sign';
  widgetShouldBeShown = false;
  chatbotClicked = false;
  chatbotStatus = '';
  private subscriptions: Subscription = new Subscription();
  private tabListenerCleanup: (() => void) | null = null;

  constructor(
    private livechatService: LivechatService,
    private translateService: TranslateService,
    private renderer: Renderer2,
  ) {
    this.livechatService.widgetVisibility$.subscribe(isVisible => {
      this.widgetShouldBeShown = isVisible;
      this.updateWidgetStatus(isVisible);
    });

    const chatbotClicked = localStorage.getItem('chatbotClicked');
    if (chatbotClicked) {
      this.chatbotClicked = true;
    }
  }

  get isChatOpen(): boolean {
    return this.chatbotStatus === 'open';
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isChatOpen) {
      this.toggleChatBot('close');
    }
  }

  private onTab = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;
    const widgetEl = document.getElementById(this.widgetID);
    if (!widgetEl) return;
    const focusable = this.getFocusableElements(widgetEl);
    if (focusable.length < 2) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = (widgetEl as any).shadowRoot?.activeElement ?? document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  toggleChatBot(mode: 'open' | 'close' | 'toggle' = 'toggle') {
    this.livechatService.toggleChatBot(mode);
    localStorage.setItem('chatbotClicked', 'true');
    this.chatbotClicked = true;
    this.livechatService.closeTooltipComponent();
  }

  async ngOnInit() {
    this.subscriptions.add(
      this.livechatService.chatbotStatus$.subscribe(status => {
        const previous = this.chatbotStatus;
        this.chatbotStatus = status;
        if (status === 'open') {
          this.focusInsideWidget();
          this.tabListenerCleanup = this.renderer.listen('document', 'keydown', this.onTab);
        } else if (previous === 'open') {
          this.tabListenerCleanup?.();
          this.tabListenerCleanup = null;
          if (status === 'close') {
            this.returnFocusToButton();
          }
        }
      })
    );
  }

  async ngAfterViewInit() {
    await this.livechatService.loadChatBot();

    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.changeBotLanguage(event.lang);
    });
  }

  ngOnDestroy(): void {
    this.tabListenerCleanup?.();
    this.subscriptions.unsubscribe();
  }

  private focusInsideWidget(): void {
    const widgetEl = document.getElementById(this.widgetID);
    if (!widgetEl) return;

    const tryFocus = (): boolean => {
      const focusable = this.getFocusableElements(widgetEl);
      if (focusable.length) {
        focusable[0].focus();
        return true;
      }
      return false;
    };

    if (tryFocus()) return;

    let safetyTimer: ReturnType<typeof setTimeout>;

    const observer = new MutationObserver(() => {
      if (tryFocus()) {
        observer.disconnect();
        clearTimeout(safetyTimer);
      }
    });

    const observeOptions: MutationObserverInit = { childList: true, subtree: true };
    observer.observe(widgetEl, observeOptions);
    const shadowRoot = (widgetEl as any).shadowRoot as ShadowRoot | null;
    if (shadowRoot) {
      observer.observe(shadowRoot, observeOptions);
    } else {
      console.warn('[LivechatComponent] widget-my-sign has no open shadow root — Tab trap and focus management may not work correctly (TES-59)');
    }

    safetyTimer = setTimeout(() => {
      observer.disconnect();
      widgetEl.setAttribute('tabindex', '-1');
      widgetEl.focus();
    }, 3000);
  }

  private returnFocusToButton(): void {
    this.chatBotButtonRef?.nativeElement.focus();
  }

  private getFocusableElements(container: Element): HTMLElement[] {
    const fromLight = Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    );
    const shadowRoot = (container as any).shadowRoot as ShadowRoot | null;
    const fromShadow = shadowRoot
      ? Array.from(shadowRoot.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      : [];
    return [...fromLight, ...fromShadow];
  }

  private async updateWidgetStatus(shouldShowWidget: boolean = false) {
    try {
      const widgetElement = document.getElementById(this.widgetID);
      if (shouldShowWidget) {
        widgetElement?.classList?.remove('hidden');
        document.body?.classList?.remove('hideChatbot');
      } else {
        widgetElement?.classList?.add('hidden');
        document.body?.classList?.add('hideChatbot');
      }
    } catch (error) {
      console.error('Error updating widget status:', error);
    }
  }

  changeBotLanguage(lang: string) {
    this.livechatService.setWidgetLanguage(lang);
  }
}
